import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ekkoText } from "@/lib/ekkoDefaults";

const DAYS = [
  ["segunda", "Segunda", "Seg"], ["terca", "Terça", "Ter"], ["quarta", "Quarta", "Qua"], ["quinta", "Quinta", "Qui"],
  ["sexta", "Sexta", "Sex"], ["sabado", "Sábado", "Sáb"], ["domingo", "Domingo", "Dom"],
];
// getDay(): 0 = domingo … 6 = sábado
const todayKey = () => DAYS[(new Date().getDay() + 6) % 7][0];
const range = ({ inicio, fim }) => (inicio === fim ? inicio : `${inicio}–${fim}`);

function DaySlots({ day, classesLabel, openLabel, large = false }) {
  const classes = day?.aulas || [];
  const open = (day?.open_box || []).filter((r) => r.inicio);
  const chip = large ? "text-base px-3 py-1.5" : "text-sm px-2.5 py-1";
  if (!classes.length && !open.length) return <p className="text-[#9A9A9A] text-sm">Encerrado</p>;
  return (
    <div className="space-y-4">
      {classes.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#C9C9C9] mb-2">{classesLabel}</p>
          <ul className="flex flex-wrap gap-1.5">
            {classes.map((t) => <li key={t} className={`${chip} rounded-full bg-[#A6C48A] text-[#010000] font-bold`}>{t}</li>)}
          </ul>
        </div>
      )}
      {open.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#C9C9C9] mb-2">{openLabel}</p>
          <ul className="flex flex-wrap gap-1.5">
            {open.map((r) => <li key={range(r)} className={`${chip} rounded-full bg-[#A6C48A]/15 border border-[#A6C48A] text-[#A6C48A] font-bold whitespace-nowrap`}>{range(r)}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// Horário: separadores por dia no telemóvel (hoje já escolhido), cartões por dia no computador
export default function ScheduleSection({ schedule, content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const today = todayKey();
  const [selected, setSelected] = useState(today);
  const classesLabel = ekkoText(content, "schedule_label_classes");
  const openLabel = ekkoText(content, "schedule_label_open");

  return (
    <section id="horarios" ref={ref} className="py-24 md:py-32 bg-[#111111] relative" data-testid="schedule-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content" dangerouslySetInnerHTML={{ __html: ekkoText(content, "schedule_title") }} />
          <p className="text-[#C9C9C9] text-base md:text-lg max-w-2xl mx-auto rich-text-content" dangerouslySetInnerHTML={{ __html: ekkoText(content, "schedule_subtitle") }} />
        </motion.div>

        {/* Telemóvel: escolher o dia */}
        <div className="md:hidden">
          <div role="tablist" aria-label="Dia da semana" className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
            {DAYS.map(([key, , short]) => (
              <button
                key={key}
                role="tab"
                aria-selected={selected === key}
                onClick={() => setSelected(key)}
                className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-bold border transition-colors ${
                  selected === key ? "bg-[#A6C48A] text-[#010000] border-[#A6C48A]" : "bg-[#010000] text-[#C9C9C9] border-[#2A2A2A]"
                }`}
              >
                {short}{key === today && <span className="sr-only"> (hoje)</span>}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="mt-4 rounded-2xl border border-[#2A2A2A] bg-[#010000] p-5">
            <h3 className="text-xl font-bold text-[#F5F5F5] mb-4">
              {DAYS.find(([k]) => k === selected)[1]}
              {selected === today && <span className="ml-2 align-middle text-xs font-bold uppercase tracking-wider bg-[#A6C48A] text-[#010000] rounded-full px-2.5 py-1">Hoje</span>}
            </h3>
            <DaySlots day={schedule?.[selected]} classesLabel={classesLabel} openLabel={openLabel} large />
          </div>
        </div>

        {/* Computador: todos os dias lado a lado */}
        <div className="hidden md:grid grid-cols-4 lg:grid-cols-7 gap-3" data-testid="schedule-grid">
          {DAYS.map(([key, name], i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.05 * i }}
              className={`rounded-2xl p-4 border ${key === today ? "border-2 border-[#A6C48A] bg-[#010000]" : "border-[#2A2A2A] bg-[#010000]"}`}
            >
              <h3 className="text-lg font-bold text-[#F5F5F5] mb-3 flex items-center justify-between gap-2">
                {name}
                {key === today && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#A6C48A] text-[#010000] rounded-full px-2 py-0.5">Hoje</span>}
              </h3>
              <DaySlots day={schedule?.[key]} classesLabel={classesLabel} openLabel={openLabel} />
            </motion.div>
          ))}
        </div>

        <p className="text-center text-[#9A9A9A] text-sm mt-8">
          {openLabel}: espaço disponível para treinares ao teu ritmo nos horários indicados.
        </p>
      </div>
      <div className="section-divider mt-24" />
    </section>
  );
}
